import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import colors from '../../../../../constants/colors';
import stretchBuilder from '../../../../../utils/animations';

import Link from '../../../../Link';

const ScrollRail = styled.div`
  position: relative;
  margin: 0 -4px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 10px;
    width: 56px;
    pointer-events: none;
    background: linear-gradient(to left, ${colors.main.white}, ${rgba(colors.main.white, 0)});
    z-index: 3;
  }

  @media screen and (max-width: 550px) {
    &::after {
      width: 36px;
    }
  }
`;

const Programs = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x proximity;
  scroll-padding-left: 4px;
  padding: 4px 4px 10px;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
    transition: background 0.2s ease;
  }

  &:hover {
    scrollbar-color: ${rgba(colors.main.grey, 0.35)} transparent;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: ${rgba(colors.main.grey, 0.35)};
  }

  @media (hover: none) {
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media screen and (max-width: 550px) {
    & > :only-child {
      margin: 0 auto;
    }
  }
`;

const Program = styled(Link)`
  cursor: pointer;
  display: block;
  flex: 0 0 364px;
  width: 364px;
  height: 212px;
  position: relative;
  background-color: ${colors.main.dividers};
  background-position: 50% 50%;
  background-repeat: no-repeat;
  background-size: cover;
  scroll-snap-align: start;
  transition:
    transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
  will-change: transform;

  &:hover,
  &:focus-visible {
    transform: translateY(-4px);
    box-shadow: 0 18px 32px -14px ${rgba(colors.main.black, 0.3)};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }

  ${(props) =>
    props.locked &&
    css`
      img {
        filter: grayscale(1) brightness(0.85) contrast(0.95);
      }
    `}

  &:before {
    content: '';
    position: absolute;
    display: block;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    opacity: 0.5;
    z-index: 1;

    ${(props) => {
      if (props.locked) {
        return css`
          background: linear-gradient(
            to top,
            ${rgba(colors.main.black, 0.55)} 0%,
            ${rgba(colors.main.black, 0.25)} 100%
          );
          opacity: 1;
        `;
      }
      if (props.isLooping) {
        return css`
          background-color: ${rgba(colors.main.lightBlue, 0.7)};
          background: linear-gradient(
            35deg,
            ${rgba(colors.main.ruby, 0.5)} 16%,
            ${rgba(colors.main.blue, 0.5)} 64%
          );
          opacity: 1;
        `;
      }
      if (props.sessionDaysCompleted) {
        return css`
          animation: ${stretchBuilder(props.sessionDaysCompleted)} 2s ease-in-out;
          animation-fill-mode: forwards;
          background-color: ${rgba(colors.main.lightBlue, 0.7)};
          background: linear-gradient(
            35deg,
            ${rgba(colors.main.ruby, 0.5)} 16%,
            ${rgba(colors.main.blue, 0.5)} 64%
          );
          opacity: 1;
          right: initial;
        `;
      }
      return css`
        background: linear-gradient(
          to top,
          ${rgba(colors.main.ruby, 0.5)} 16%,
          ${rgba(colors.main.blue, 0.5)} 64%
        );
      `;
    }}
  }

  @media screen and (max-width: 992px) {
    flex: 0 0 340px;
    width: 340px;
    height: 240px;
  }

  @media screen and (max-width: 550px) {
    flex: 0 0 78vw;
    width: 78vw;
    height: 240px;
  }
`;

const Description = styled.div`
  bottom: 13px;
  left: 20px;
  right: 20px;
  position: absolute;
  z-index: 100;
`;

const Image = styled.img`
  display: block;
  height: 100%;
  width: 100%;
  filter: brightness(0.8);
  object-fit: cover;
  background-color: ${colors.main.dividers};
`;

const CategorySection = styled.section`
  scroll-margin-top: 24px;

  & + & {
    margin-top: 44px;
  }

  @media screen and (max-width: 992px) {
    & + & {
      margin-top: 32px;
    }
  }
`;

const CategoryHeader = styled.header`
  align-items: baseline;
  border-bottom: 1px solid ${colors.main.dividers};
  display: grid;
  gap: 0 18px;
  grid-template-columns: auto 1fr auto;
  margin-bottom: 18px;
  padding-bottom: 14px;

  @media screen and (max-width: 550px) {
    gap: 0 12px;
    padding-bottom: 10px;
    margin-bottom: 14px;
  }
`;

const CategoryNumber = styled.span`
  font-family: 'DINNextW01-Condensed', Arial, sans-serif;
  font-size: 40px;
  font-weight: 300;
  line-height: 1;
  letter-spacing: 0.04em;
  color: ${colors.main.placeholdersAndDisabledBtnsText};
  grid-row: 1 / span 2;
  align-self: center;

  @media screen and (max-width: 550px) {
    font-size: 30px;
  }
`;

const CategoryTitleRow = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  grid-column: 2;
`;

const CategorySubtitle = styled.div`
  grid-column: 2;
  margin-top: 4px;
`;

const CategoryCount = styled.span`
  grid-column: 3;
  grid-row: 1 / span 2;
  align-self: center;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.12em;
  line-height: 1;
  text-transform: uppercase;
  color: ${colors.main.grey};
  white-space: nowrap;

  @media screen and (max-width: 550px) {
    display: none;
  }
`;

export {
  Image,
  Programs,
  Program,
  Description,
  CategorySection,
  CategoryHeader,
  CategoryNumber,
  CategoryTitleRow,
  CategorySubtitle,
  CategoryCount,
  ScrollRail,
};
